import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export interface AuthMember {
  member_id: string;
  member_name: string;
  phone: string | null;
  points_balance: number;
  discount_code: string | null;
  discount_used: boolean;
}

interface AuthContextType {
  member: AuthMember | null;
  isLoggedIn: boolean;
  login: (memberId: string, password: string) => Promise<{ success: boolean; msg: string }>;
  logout: () => void;
  refreshMember: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [member, setMember] = useState<AuthMember | null>(() => {
    try {
      const saved = localStorage.getItem('auth_member');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const isLoggedIn = member !== null;

  const login = async (memberId: string, password: string): Promise<{ success: boolean; msg: string }> => {
    try {
      const cleanId = memberId.trim().toUpperCase();
      const { data, error } = await supabase
        .from('members')
        .select('member_id, member_name, phone, points_balance, discount_code, discount_used, password_hash, is_withdrawn')
        .eq('member_id', cleanId)
        .maybeSingle();

      if (error || !data) {
        return { success: false, msg: '등록되지 않은 회원ID입니다.' };
      }

      if (data.is_withdrawn) {
        return { success: false, msg: '탈퇴한 회원입니다. 로그인이 불가합니다.' };
      }

      // 비밀번호 확인
      if (data.password_hash) {
        if (data.password_hash !== password) {
          return { success: false, msg: '비밀번호가 올바르지 않습니다.' };
        }
      } else {
        // 구버전 호환: password_hash 없으면 전화번호로 확인
        if (!data.phone || data.phone.replace(/-/g, '') !== password.replace(/-/g, '')) {
          return { success: false, msg: '비밀번호가 올바르지 않습니다.' };
        }
      }

      const authMember: AuthMember = {
        member_id: data.member_id,
        member_name: data.member_name,
        phone: data.phone,
        points_balance: data.points_balance,
        discount_code: data.discount_code,
        discount_used: data.discount_used ?? false,
      };

      setMember(authMember);
      localStorage.setItem('auth_member', JSON.stringify(authMember));
      return { success: true, msg: `${data.member_name}님, 환영합니다!` };
    } catch {
      return { success: false, msg: '로그인 중 오류가 발생했습니다.' };
    }
  };

  const logout = () => {
    setMember(null);
    localStorage.removeItem('auth_member');
  };

  const refreshMember = async () => {
    if (!member) return;
    try {
      const { data } = await supabase
        .from('members')
        .select('member_id, member_name, phone, points_balance, discount_code, discount_used')
        .eq('member_id', member.member_id)
        .maybeSingle();

      if (data) {
        const updated: AuthMember = {
          member_id: data.member_id,
          member_name: data.member_name,
          phone: data.phone,
          points_balance: data.points_balance,
          discount_code: data.discount_code,
          discount_used: data.discount_used ?? false,
        };
        setMember(updated);
        localStorage.setItem('auth_member', JSON.stringify(updated));
      }
    } catch {
      // silent
    }
  };

  // 앱 시작 시 최신 포인트 동기화
  useEffect(() => {
    if (member) {
      refreshMember();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ member, isLoggedIn, login, logout, refreshMember }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
